import { detailParams, docParams } from 'src/types/ICompany';
import {
  DEFAULT_EMPTY_VALUE,
  PROVIDER_SOURCES,
  ITALY_COUNTRY_CODE,
  ITALY_TYPE_DNB_CODE,
} from 'src/constants/companyReport';
import { companyReportsAxiosInstance } from './axiosInstance.service';

export const getBasicDetail = (detailParams: detailParams) => {
  const queryParams = new URLSearchParams({
    ...(detailParams.threshold && { threshold: detailParams.threshold.toString() }), // Include threshold if it exists
    ...(detailParams.screening && { screening: detailParams.screening.toString() }), // Include screening if it exists
    ...(detailParams.regAuth && { regAuth: detailParams.regAuth.toString() }), // Include regAuth if it exists
    ...(detailParams.stateCode && { stateCode: detailParams.stateCode.toString() }),
  }).toString();
  return companyReportsAxiosInstance.get(
    `/company/profile/${detailParams.countryISO}/${detailParams.companyCode}${
      queryParams ? `?${queryParams}` : ''
    }`,
  );
};

export const getDocuments = (params: docParams, regAuth?: string) => {
  const queryParams = regAuth ? { RegAuth: regAuth } : {};

  return companyReportsAxiosInstance.get(`/documents/list/${params.countryISO}/${params.companyCode}`, {
    params: queryParams,
  });
};

/**
 * This function sends API request to Company Reports backend for registering order kyckr document
 * @param productKey
 */
export const orderDocument = (productKey: string) => {
  return companyReportsAxiosInstance.post(
    '/documents/order',
    { productKey },
  );
};

/**
 * This function sends API request PDF Document Report based on company report input
 * @param productKey
 */
export const getCompanyReportPDF = (detailData: any) => {
  return companyReportsAxiosInstance.post(
    '/documents/company-report-pdf',
    { detailData },
    {
      responseType: 'blob',
    },
  );
};

export const formatDate = (date: string): string => {
  if (!date) {
    return DEFAULT_EMPTY_VALUE;
  }

  // date string has only year
  if (isYearOnly(date)) {
    return date;
  }

  let dt = new Date(date);

  if (isNaN(dt.getTime()) && date.includes('/')) {
    return date;
  }

  if (isNaN(dt.getTime()) && !date.includes('/')) {
    return DEFAULT_EMPTY_VALUE;
  }

  // date string has only year and month
  if (hasYearAndMonth(date)) {
    return `${dt.getMonth() + 1}/${dt.getFullYear()}`;
  }

  return `${dt.getDate()}/${dt.getMonth() + 1}/${dt.getFullYear()}`;
};

export const isYearOnly = (date: string): boolean => {
  const yearRegex = /^\d{4}$/;
  return yearRegex.test(date);
};

export const hasYearAndMonth = (date: string): boolean => {
  const yearMonthRegex = /^\d{4}[-\/]\d{1,2}$/;
  return yearMonthRegex.test(date);
};

export const getMatchByName = (name: string, entities: any[]) => {
  if (entities?.length) {
    for (let entity of entities) {
      if (entity?.name?.toLowerCase() === name?.toLowerCase()) {
        return entity.match;
      }
    }
  }
};

export const combineNames = (director: any) => {
  let fullName = '';

  if (!director) return fullName;

  if (director.firstName) {
    fullName += director.firstName;
  }

  if (director.middleName) {
    if (fullName) {
      fullName += ' ' + director.middleName;
    } else {
      fullName = director.middleName;
    }
  }

  if (director.lastName) {
    if (fullName) {
      fullName += ' ' + director.lastName;
    } else {
      fullName = director.lastName;
    }
  }

  return fullName ? fullName : director?.name;
};

export const getNominalShareCapital = (nominalShareCapital: any) => {
  if (!nominalShareCapital) return DEFAULT_EMPTY_VALUE;
  const currency = nominalShareCapital.currency ? `(${nominalShareCapital.currency})` : '';
  return `${formatNumber(nominalShareCapital.value)} ${currency}`;
};

/**
 * Create tooltip content for registration number
 *
 * @param detailData details from api response includes companyProfile information
 * @returns toolTip
 */
export const getRegistrationNumberTooltip = (detailData: any): string => {
  let toolTip = '';
  if (detailData?.source === PROVIDER_SOURCES.DNB) {
    let { allAvailableRegistrationNumbers } = detailData?.companyProfile?.companyProfile;

    toolTip = allAvailableRegistrationNumbers?.length
      ? allAvailableRegistrationNumbers
          ?.map(
            (registrationNumber: any) =>
              `RegNo:${registrationNumber?.registrationNumber},Desc: ${
                registrationNumber?.typeDescription
              }, Preferred: ${registrationNumber?.isPreferredRegistrationNumber ?? 'N/A'}`,
          )
          .join('\n')
      : '';
  } else {
    toolTip = detailData?.companyProfile?.companyProfile?.companyRegistrationNumber?.length
      ? detailData?.companyProfile?.companyProfile?.companyRegistrationNumber?.join(',')
      : '';
  }
  return toolTip;
};

/**
 * select preferred registration number from list if not then returns the first one
 *
 * @param registrationNumbers list of registration numbers
 * @param companyName company Name to be used as fallback
 * @returns registration number
 */
export const getPreferredRegistrationNumber = (
  registrationNumbers: any,
  __: string,
  countryISO: string,
) => {
  let preferredRegistrationNumber = registrationNumbers?.find(
    (registrationNumbers: any) => registrationNumbers?.isPreferredRegistrationNumber === true,
  )?.registrationNumber;
  //In case of italy use "typeDnBCode":
  if (countryISO === ITALY_COUNTRY_CODE) {
    preferredRegistrationNumber = registrationNumbers?.find(
      (registrationNumbers: any) => registrationNumbers?.typeDnBCode === ITALY_TYPE_DNB_CODE,
    )?.registrationNumber;
  }

  if (!preferredRegistrationNumber && registrationNumbers?.length) {
    preferredRegistrationNumber = registrationNumbers[0]?.registrationNumber;
  }

  return preferredRegistrationNumber ? preferredRegistrationNumber : DEFAULT_EMPTY_VALUE;
};
export const formatNumber = (inputNumber: string | number, locale = 'en-GB'): string => {
  if (inputNumber === null || inputNumber === undefined) {
    return DEFAULT_EMPTY_VALUE;
  }

  if (typeof inputNumber !== 'number') {
    inputNumber = parseFloat(inputNumber);
    if (isNaN(inputNumber)) {
      return DEFAULT_EMPTY_VALUE;
    }
  }

  return inputNumber.toLocaleString(locale);
};
